import { CustomListKey, Tenant } from "@sp-crm/core";
import { SupportEmailLink } from "components/app/support-email-link";
import { UpsellBanner } from "components/app/upsell-banner";
import { Feature } from "components/feature";
import { FileSelect, SelectableFile } from "components/files/file-select";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { trpcClient } from "components/rpc/trpc";
import { NumberInput } from "components/shared/number-input";
import { InlineEmailTemplateSetting } from "components/templates/inline-email-template-setting";
import { AutosavingInput } from "components/ui/autosaving-input";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Select } from "components/ui/select";
import { Toggle } from "components/ui/toggle";
import { GetCustomListQuery } from "generated/graphql";
import React, { useCallback } from "react";
import { useDispatch } from "react-redux";
import { updateTenant } from "store/actions";
import { useProductName } from "store/selectors/branding";
import { useCurrentTenant, useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { handleEvent } from "util/user-events";
import { AgencySettingsOngoingPref } from "./agency-settings-ongoing";
import { AgencyTogglePref } from "./agency-settings-toggle";
import { CustomListEditor } from "./custom-list-editor";

const isPdfFile = (file: SelectableFile): boolean => {
    const pdfMimeTypes = [
        "application/pdf",
        "application/x-pdf",
        "application/acrobat",
        "applications/vnd.pdf",
        "text/pdf",
    ];

    if (file.mimeType && pdfMimeTypes.includes(file.mimeType)) {
        return true;
    }

    if (file.originalFilename && file.originalFilename.endsWith(".pdf")) {
        return true;
    }

    return false;
};

const getClientStatusHelpText = (
    item: GetCustomListQuery["getCustomList"]["items"][0],
): string => {
    switch (item.key) {
        case "new":
            return "New clients will have this status";
        case "scheduled":
            return "For clients scheduled to move into a community";
        case "invoiced":
            return "To denote when an invoice has been sent for services";
        case "moved":
            return "For clients who have moved into a community";
        case "paid":
            return "For placements that have been fully paid";
        case "checkLater":
            return "For clients who should be checked on later";
        default:
            return "";
    }
};

export const AgencySettingsClients: React.FC<unknown> = () => {
    const clientDisclosuresEnabled = useFeature("clientDisclosures");
    const customClientStatusEnabled = useFeature("customizeClientStatus");
    const clientCollaboratorsFeatureEnabled = useFeature("clientCollaborators");
    const trackTimeFeature = useFeature("trackTime");
    const tenant = useCurrentTenant();
    const dispatch = useDispatch();
    const updatePdfDisclaimer = useCallback(
        (val: string) => {
            const reloadedTenant = Tenant.fromDocument(tenant);
            reloadedTenant.tenantId = tenant.tenantId;
            reloadedTenant.pdfDisclaimer = val;
            updateTenant(reloadedTenant, dispatch);
        },
        [tenant, dispatch],
    );
    const { settings, putSetting } = useTenantSettings();
    const productName = useProductName();

    const handleTimeTrackingToggle = useCallback(
        async (isChecked: boolean) => {
            if (!isChecked) {
                const confirmed = await fancyConfirm(
                    "Disable time tracking",
                    "Any active timers across your agency will be lost. Are you sure you want to disable time tracking?",
                    "Disable",
                    "Cancel",
                );
                if (!confirmed) return;
                await trpcClient.activeTimers.clearAll.mutate({});
            }
            putSetting("trackTimeEnabled", isChecked);
            handleEvent(isChecked ? "time-tracking-enabled" : "time-tracking-disabled");
        },
        [putSetting],
    );

    return (
        <Feature name="placement">
            <div className="space-y-4 lg:space-y-8">
                <ContentFull>
                    <ContentHeader>Clients</ContentHeader>
                    <div className="space-y-4">
                        <AgencyTogglePref
                            field="showClientFirst"
                            label="Show Client Info first, before the Best Contact Details"
                            description="Wherever Client and Best Contact info is shown, the Client info will show first."
                        />

                        <AgencyTogglePref
                            field="showClientSerial"
                            label="Generate ID number for clients"
                            description="Give each client an ID number that can be used to quickly find them in the system."
                        />

                        {customClientStatusEnabled ? null : <AgencySettingsOngoingPref />}

                        <NumberInput
                            label="Number of days a client is visible on the dashboard after payment received"
                            initial={settings.closedPaidDuration}
                            onCommit={d => putSetting("closedPaidDuration", d)}
                        />

                        {clientCollaboratorsFeatureEnabled ? (
                            <AgencyTogglePref
                                field="clientCollaborators"
                                label="Enable Client Collaborators"
                                description="Allow users to be added as collaborators on client records, giving them view or edit access when otherwise restricted."
                            />
                        ) : null}
                    </div>
                </ContentFull>
                {trackTimeFeature ? (
                    <ContentFull>
                        <ContentHeader>Time Tracking</ContentHeader>
                        <div className="group">
                            <Toggle
                                checked={settings.trackTimeEnabled}
                                onChange={handleTimeTrackingToggle}
                                label={
                                    <div>
                                        <span className="text-md">
                                            Enable time tracking
                                        </span>
                                        <div className="text-gray-500 group-hover:text-gray-900 text-sm">
                                            When enabled, users can track billable hours
                                            on each client.
                                        </div>
                                    </div>
                                }
                            />
                        </div>
                        {settings.trackTimeEnabled ? (
                            <div className="mt-4 space-y-4">
                                <NumberInput
                                    label="Billing increment (minutes)"
                                    initial={settings.timeTrackingIncrementMinutes || 15}
                                    onCommit={value =>
                                        putSetting("timeTrackingIncrementMinutes", value)
                                    }
                                />
                            </div>
                        ) : null}
                    </ContentFull>
                ) : null}
                <ContentFull>
                    <ContentHeader>Client Overview</ContentHeader>
                    <div className="space-y-4">
                        <AgencyTogglePref
                            field="showClientIntakeWeight"
                            label="Show Client Weight Field"
                            description='On the Overview page, show the "Weight" field.'
                        />

                        <AgencyTogglePref
                            field="showClientIntakeHeight"
                            label="Show Client Height Field"
                            description='On the Overview page, show the "Height" field.'
                        />
                    </div>
                </ContentFull>
                <ContentFull>
                    <ContentHeader>Client Intake Details</ContentHeader>
                    <div className="space-y-4">
                        <AgencyTogglePref
                            field="showClientMonthlyBudget"
                            label="Show Client Monthly Budget Field"
                            description='On the Intake Details page, show the "Monthly Budget" field.'
                        />

                        <AgencyTogglePref
                            field="showClientTotalFinances"
                            label="Show Client Total Finances Field"
                            description='On the Intake Details page, show the "Total Finances" field.'
                        />

                        <AgencyTogglePref
                            field="showPhysicianContacts"
                            label="Show Care Providers section"
                            description='On the Intake Details page, show the "Care Providers" section.'
                        />

                        <AgencyTogglePref
                            field="showInsuranceInfo"
                            label="Show Health Insurance section"
                            description='On the Intake Details page, show the "Health Insurance" section.'
                        />
                    </div>
                </ContentFull>
                {clientDisclosuresEnabled ? (
                    <ContentFull>
                        <ContentHeader>Disclosures</ContentHeader>
                        <div className="space-y-4">
                            <FileSelect
                                label="Choose PDF to use for client disclosures"
                                description="To add a PDF to this list, upload it using the Files tab"
                                fileFilter={isPdfFile}
                                value={settings.clientDisclosureFileId}
                                onChange={id => {
                                    putSetting("clientDisclosureFileId", id);
                                }}
                            />
                            <InlineEmailTemplateSetting
                                subjectLabel="Client disclosure request subject"
                                bodyLabel="Client disclosure request body"
                                value={settings.clientDisclosureSendTemplateId}
                                onChange={id => {
                                    putSetting("clientDisclosureSendTemplateId", id);
                                }}
                            />
                            <InlineEmailTemplateSetting
                                subjectLabel="Disclosure acknowledgement thank you subject"
                                bodyLabel="Disclosure acknowledgement thank you body"
                                value={settings.clientDisclosureReceiptTemplateId}
                                onChange={id => {
                                    putSetting("clientDisclosureReceiptTemplateId", id);
                                }}
                            />
                        </div>
                    </ContentFull>
                ) : null}
                <ContentFull>
                    <ContentHeader>Referral PDF</ContentHeader>
                    <ContentSubheader>Outbound referral to communities.</ContentSubheader>
                    <div className="space-y-4">
                        <AutosavingInput
                            label="Client Referral PDF Disclaimer"
                            multiLine={true}
                            initial={tenant.pdfDisclaimer ?? ""}
                            onCommit={updatePdfDisclaimer}
                            disabled={settings.intakeDisclosureLocation === "none"}
                        />
                        <Select
                            label="Disclaimer location"
                            onChange={e =>
                                putSetting("intakeDisclosureLocation", e.target.value)
                            }
                            value={settings.intakeDisclosureLocation}>
                            <option value="footer">Bottom of last page</option>
                            <option value="header">Top of first page</option>
                            <option value="both">
                                Both top of first page and bottom of last page
                            </option>
                            <option value="none">Do not include disclaimer</option>
                        </Select>
                    </div>
                </ContentFull>
                <ContentFull>
                    <ContentHeader>Client Statuses</ContentHeader>
                    <ContentSubheader>
                        Customize client status labels, order, and categories to support
                        your placement workflow. Some statuses (e.g. New, Moved, Closed -
                        Paid) have specific purposes for {productName} functionality, so
                        make sure their names reflect their intended use.
                    </ContentSubheader>
                    {customClientStatusEnabled ? null : (
                        <UpsellBanner source="client-status">
                            <InlineBanner type="info">
                                Client status customization is available to {productName}{" "}
                                Premium customers. Contact <SupportEmailLink /> to discuss
                                an upgrade to Premium.
                            </InlineBanner>
                        </UpsellBanner>
                    )}
                    <CustomListEditor
                        disabled={!customClientStatusEnabled}
                        customListKey={CustomListKey.ClientStatus}
                        getHelpText={getClientStatusHelpText}
                    />
                </ContentFull>
            </div>
        </Feature>
    );
};
